import Toggle, { ToggleProps } from 'react-toggle';
import styled, { css } from 'styled-components';

const ToggleButtonStyle = styled.div<{ $gradient?: boolean }>`
  .react-toggle {
    touch-action: pan-x;
    display: inline-block;
    position: relative;
    cursor: pointer;
    background-color: transparent;
    border: 0;
    padding: 0;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    -webkit-tap-highlight-color: transparent;
  }
  .react-toggle-screenreader-only {
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }
  .react-toggle--disabled {
    cursor: not-allowed;
    opacity: 0.5;
    -webkit-transition: opacity 0.25s;
    transition: opacity 0.25s;
  }
  .react-toggle-track {
    width: 50px;
    height: 24px;
    padding: 0;
    border-radius: 30px;
    background-color: #4d4d4d;
    -webkit-transition: all 0.2s ease;
    -moz-transition: all 0.2s ease;
    transition: all 0.2s ease;
  }
  .react-toggle:hover:not(.react-toggle--disabled) .react-toggle-track {
    background-color: #4c4c4c;
  }
  .react-toggle--checked .react-toggle-track {
    background-color: ${({ theme }) => theme.primary};
  }
  .react-toggle--checked:hover:not(.react-toggle--disabled)
    .react-toggle-track {
    background-color: ${({ theme }) => theme.primary};
  }
  .react-toggle-track-check {
    position: absolute;
    width: 14px;
    height: 10px;
    top: 0px;
    bottom: 0px;
    margin-top: auto;
    margin-bottom: auto;
    line-height: 0;
    left: 8px;
    opacity: 0;
    -webkit-transition: opacity 0.25s ease;
    -moz-transition: opacity 0.25s ease;
    transition: opacity 0.25s ease;
  }
  .react-toggle--checked .react-toggle-track-check {
    opacity: 1;
    -webkit-transition: opacity 0.25s ease;
    -moz-transition: opacity 0.25s ease;
    transition: opacity 0.25s ease;
  }
  .react-toggle-track-x {
    position: absolute;
    width: 10px;
    height: 10px;
    top: 0px;
    bottom: 0px;
    margin-top: auto;
    margin-bottom: auto;
    line-height: 0;
    right: 10px;
    opacity: 1;
    -webkit-transition: opacity 0.25s ease;
    -moz-transition: opacity 0.25s ease;
    transition: opacity 0.25s ease;
  }
  .react-toggle--checked .react-toggle-track-x {
    opacity: 0;
  }
  .react-toggle-thumb {
    transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1) 0ms;
    position: absolute;
    top: 1px;
    left: 1px;
    width: 22px;
    height: 22px;
    border: 1px solid #4d4d4d;
    border-radius: 50%;
    background-color: #fafafa;
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    -webkit-transition: all 0.25s ease;
    -moz-transition: all 0.25s ease;
    transition: all 0.25s ease;
  }
  .react-toggle--checked .react-toggle-thumb {
    left: 27px;
    border-color: ${({ theme }) => theme.primary};
  }
  .react-toggle--focus .react-toggle-thumb {
    -webkit-box-shadow: 0px 0px 3px 2px ${({ theme }) => theme.primary};
    -moz-box-shadow: 0px 0px 3px 2px ${({ theme }) => theme.primary};
    box-shadow: 0px 0px 2px 3px ${({ theme }) => theme.primary};
  }
  .react-toggle:active:not(.react-toggle--disabled) .react-toggle-thumb {
    -webkit-box-shadow: 0px 0px 5px 5px ${({ theme }) => theme.primary};
    -moz-box-shadow: 0px 0px 5px 5px ${({ theme }) => theme.primary};
    box-shadow: 0px 0px 5px 5px ${({ theme }) => theme.primary};
  }

  ${({ $gradient }) =>
    $gradient &&
    css`
      /* AI variant: checked track uses the blue→purple→magenta gradient (like the
         gradient button) with a soft glow, replacing the primary-red fill. */
      .react-toggle--checked .react-toggle-track,
      .react-toggle--checked:hover:not(.react-toggle--disabled)
        .react-toggle-track {
        background: linear-gradient(
          90deg,
          #306fde 0.11%,
          #3a6bdb 32.44%,
          #7f50c7 68.63%,
          #ba39b5 100.48%
        );
        box-shadow: 0 0 8px 1px rgba(127, 80, 199, 0.6),
          0 0 16px 3px rgba(186, 57, 181, 0.35);
      }
      .react-toggle--checked .react-toggle-thumb {
        border-color: #7f50c7;
      }
      .react-toggle--focus .react-toggle-thumb {
        -webkit-box-shadow: 0px 0px 2px 3px #7f50c7;
        -moz-box-shadow: 0px 0px 2px 3px #7f50c7;
        box-shadow: 0px 0px 2px 3px #7f50c7;
      }
      .react-toggle:active:not(.react-toggle--disabled) .react-toggle-thumb {
        -webkit-box-shadow: 0px 0px 5px 5px rgba(127, 80, 199, 0.6);
        -moz-box-shadow: 0px 0px 5px 5px rgba(127, 80, 199, 0.6);
        box-shadow: 0px 0px 5px 5px rgba(127, 80, 199, 0.6);
      }
    `}
`;

type AdminSwitchProps = ToggleProps & {
  /**
   * **Required for accessibility.** Without this, screen readers
   * announce only "switch, on" — users have no idea what's being
   * toggled. Pass a descriptive label, e.g. `"Enable notifications"`.
   *
   * Alternatively, supply `aria-labelledby` referencing an existing
   * visible label's id.
   *
   * Typed optional only because ~32 existing call sites need a
   * gradual migration; new code should always supply one.
   */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** AI variant: gradient + glow track when checked (menu-ai toggles). */
  $gradient?: boolean;
};

export const AdminSwitch = ({ $gradient, ...props }: AdminSwitchProps) => {
  return (
    <ToggleButtonStyle $gradient={$gradient}>
      <Toggle {...props} icons={false} />
    </ToggleButtonStyle>
  );
};
